import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../../sub-comp/Navbar";
import Footer from "../../sub-comp/MainFooter";

const CourseDetailPage = ({
  backLink,
  heroTitle,
  heroSubtitle,
  heroImage,
  introParagraphs,
  learningOutcomes,
  careerPaths,
  prerequisites,
  duration,
  level,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-white text-black overflow-hidden relative">
        {/* Hero Section */}
        <section
          className={`relative h-[70vh] flex items-center justify-center transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <img
            src={heroImage}
            alt={heroTitle}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">{heroTitle}</h1>
            <p className="text-lg md:text-xl max-w-2xl mx-auto">{heroSubtitle}</p>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
          {/* Breadcrumb */}
          <div className="bg-white">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <Link
                to={backLink}
                className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                Back to Course Programs
              </Link>
            </div>
          </div>

          {/* Course Info Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-custom-pink/10 to-purple-100 rounded-xl p-6 border border-custom-pink/20">
              <h3 className="text-lg font-bold text-custom-pink mb-2">Duration</h3>
              <p className="text-gray-700">{duration}</p>
            </div>
            <div className="bg-gradient-to-br from-gold/10 to-yellow-100 rounded-xl p-6 border border-gold/20">
              <h3 className="text-lg font-bold text-gold mb-2">Level</h3>
              <p className="text-gray-700">{level}</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-100 to-blue-100 rounded-xl p-6 border border-indigo-200">
              <h3 className="text-lg font-bold text-indigo-600 mb-2">Prerequisites</h3>
              <p className="text-gray-700">{prerequisites}</p>
            </div>
          </div>

          {/* Introduction Section */}
          <section
            ref={sectionRef}
            className={`mb-16 transition-all duration-1000 delay-300 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-custom-pink mb-8 text-center">
              Course Overview
            </h2>
            <div className="space-y-6">
              {introParagraphs.map((text, idx) => (
                <p key={idx} className="text-lg leading-relaxed text-gray-700">
                  {text}
                </p>
              ))}
            </div>
          </section>

          {/* Learning Outcomes Section */}
          <section
            className={`mb-16 transition-all duration-1000 delay-500 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <div className="bg-gradient-to-r from-custom-pink to-purple-600 rounded-2xl p-8 text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
                What You'll Learn
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {learningOutcomes.map((outcome, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white text-sm">✓</span>
                    </div>
                    <p className="text-white/90 leading-relaxed">{outcome}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Career Paths Section */}
          <section
            className={`mb-16 transition-all duration-1000 delay-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-custom-pink mb-8 text-center">
              Career Opportunities
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {careerPaths.map((career, idx) => (
                <div
                  key={idx}
                  className={`${career.color} rounded-2xl p-6 text-white shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-500 cursor-pointer group`}
                >
                  <h3 className="text-xl font-bold mb-3 group-hover:scale-105 transition-transform duration-300">
                    {career.title}
                  </h3>
                  <p className="text-white/90 leading-relaxed mb-4">{career.description}</p>
                  <div className="w-8 h-1 bg-white/50 rounded-full group-hover:w-16 transition-all duration-300" />
                </div>
              ))}
            </div>
          </section>

          {/* Call to Action */}
          <section
            className={`text-center transition-all duration-1000 delay-900 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 border border-gray-200">
              <h2 className="text-3xl font-bold text-custom-pink mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Join our comprehensive program and gain the skills needed to excel in this field.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-custom-pink text-white px-8 py-3 rounded-full font-semibold hover:bg-purple-600 transition-colors duration-300"
                >
                  Apply Now
                </Link>
                <Link
                  to="/fees"
                  className="bg-gold text-black px-8 py-3 rounded-full font-semibold hover:bg-yellow-500 transition-colors duration-300"
                >
                  View Fees
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CourseDetailPage;

